import React, { useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Link, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import Header from '../components/header';
import { CtaButton, Eyebrow, Lead, MarkerList, Mono, Section, SectionHeading } from '../components/section';
import HeroVisual from '../components/hero-visual';
import Intro from '../components/intro';
import Footer from '../components/footer';
import { brand } from '../thema';
import { useTranslation } from 'react-i18next';
import useLanguage from '../useLanguage';

type Described = { title: string; description: string };
type ScreenRow = { size: string; price: string; example: string };
type SimulationRow = { item: string; quantity: string; amount: string };
type TechGroup = { label: string; items: string[]; note?: string };
type FaqItem = { question: string; answer: string };
type WorkItem = { type: string; name: string; description: string; period: string; budget: string; tech: string[] };
type Stat = { value: string; label: string };

const pad = (n: number) => String(n).padStart(2, '0');

const HomePage = () => {
    const { t, i18n } = useTranslation();
    const { language } = useLanguage();
    const { hash } = useLocation();

    // i18n の配列・オブジェクトを取り出す
    const list = <T,>(key: string) => t(key, { returnObjects: true }) as unknown as T[];

    useEffect(() => {
        i18n.changeLanguage(language);
    }, []);

    // 他ページから /#pricing 等で遷移してきた場合に該当セクションへスクロール
    useEffect(() => {
        if (!hash) return;
        const target = document.getElementById(hash.slice(1));
        if (target) target.scrollIntoView();
    }, [hash]);

    const cardSx = {
        border: `1px solid ${brand.line}`,
        backgroundColor: brand.paper,
        p: { xs: 3, md: 4 },
    };

    return (
        <Box sx={{ color: brand.ink }}>
            <Intro />
            <Header dark />
            <Box component="main">

                {/* 1. Hero */}
                <Box
                    component="section"
                    id="top"
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#ffffff',
                        backgroundColor: brand.dark,
                        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), radial-gradient(ellipse 60% 50% at 80% 40%, rgba(69, 182, 175, 0.14), transparent 70%)',
                        backgroundSize: '56px 56px, 56px 56px, 100% 100%',
                        backgroundPosition: 'center',
                    }}
                >
                    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 }, pt: { xs: 16, md: 18 }, pb: { xs: 10, md: 12 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'center', gap: { xs: 8, md: 6 } }}>
                        <Box>
                            <Eyebrow label={t('home.hero.eyebrow')} tone="dark" />
                            <Typography
                                component="h1"
                                sx={{
                                    mt: 3,
                                    fontSize: { xs: '2rem', sm: '3rem', md: '3.25rem', lg: '3.5rem' },
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    letterSpacing: '0.02em',
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                {t('home.hero.title')}
                            </Typography>
                            <Lead tone="dark">{t('home.hero.lead')}</Lead>

                            <Box sx={{ mt: { xs: 5, md: 6 }, pl: 2.5, borderLeft: `2px solid ${brand.accent}` }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 2, rowGap: 0.5 }}>
                                    <Typography component="span" sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.72)' }}>
                                        {t('home.hero.priceLabel')}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 500, letterSpacing: '0.02em' }}>
                                        {t('home.hero.price')}
                                    </Typography>
                                    <Typography component="span" aria-hidden sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: { xs: '1.5rem', md: '1.75rem' }, color: 'rgba(255, 255, 255, 0.35)' }}>
                                        /
                                    </Typography>
                                    <Typography component="span" sx={{ flexBasis: { xs: '100%', sm: 'auto' }, fontSize: { xs: '1.375rem', md: '1.75rem' }, fontWeight: 500 }}>
                                        {t('home.hero.period')}
                                    </Typography>
                                </Box>
                                <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                                    {t('home.hero.note')}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <CtaButton href="/contact" tone="dark">{t('home.hero.ctaPrimary')}</CtaButton>
                                <CtaButton href="#pricing" tone="dark" variant="secondary">{t('home.hero.ctaSecondary')}</CtaButton>
                            </Box>

                            <Link
                                href="#capacity"
                                underline="none"
                                sx={{ mt: { xs: 5, md: 6 }, display: 'inline-flex', alignItems: 'center', gap: 1.5, color: 'rgba(255, 255, 255, 0.72)', fontSize: '0.8125rem', letterSpacing: '0.06em', '&:hover': { color: '#ffffff' } }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, backgroundColor: brand.accent }} />
                                {t('home.hero.capacity')}
                            </Link>
                        </Box>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
                            <HeroVisual />
                        </Box>
                    </Container>
                </Box>

                {/* 2. 当社の開発について */}
                <Section id="about">
                    <SectionHeading index="01" eyebrow={t('home.about.eyebrow')} title={t('home.about.title')} lead={t('home.about.lead')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 4, md: 5 } }}>
                        {list<Described>('home.about.values').map((value, i) => (
                            <Box key={value.title} sx={{ borderTop: `1px solid ${brand.ink}`, pt: 3 }}>
                                <Mono color={brand.accentText}>{pad(i + 1)}</Mono>
                                <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.25rem', fontWeight: 700 }}>{value.title}</Typography>
                                <Typography sx={{ mt: 1.5, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>{value.description}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Section>

                {/* 3. Web / Mobile 対応範囲 */}
                <Section id="services" tone="alt">
                    <SectionHeading index="02" eyebrow={t('home.services.eyebrow')} title={t('home.services.title')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                        {(['web', 'mobile'] as const).map((key) => (
                            <Box key={key} sx={cardSx}>
                                <Mono color={brand.accentText}>{t(`home.services.${key}.label`)}</Mono>
                                <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.5rem', fontWeight: 700 }}>{t(`home.services.${key}.title`)}</Typography>
                                <Typography sx={{ mt: 2, mb: 3, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>{t(`home.services.${key}.description`)}</Typography>
                                <MarkerList items={list<string>(`home.services.${key}.items`)} />
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ mt: { xs: 6, md: 8 } }}>
                        <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 3 }}>{t('home.services.targetsTitle')}</Typography>
                        <MarkerList items={list<string>('home.services.targets')} columns={{ xs: 1, md: 2 }} />
                        <Typography sx={{ mt: 4, fontSize: '0.8125rem', lineHeight: 1.8, color: brand.muted }}>{t('home.services.targetsNote')}</Typography>
                    </Box>
                </Section>

                {/* 4. なぜこの価格を実現できるのか（開発モデル） */}
                <Section id="model" tone="dark" grid>
                    <SectionHeading index="03" eyebrow={t('home.model.eyebrow')} title={t('home.model.title')} tone="dark" />
                    <Typography sx={{ pl: { xs: 2.5, md: 3 }, borderLeft: `2px solid ${brand.accent}`, fontSize: { xs: '1.375rem', md: '2.25rem' }, fontWeight: 500, lineHeight: 1.6, letterSpacing: '0.02em', whiteSpace: 'pre-line' }}>
                        {t('home.model.statement')}
                    </Typography>
                    <Box sx={{ mt: { xs: 8, md: 10 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 5 }}>
                        {list<Described>('home.model.blocks').map((block, i) => (
                            <Box key={block.title} sx={{ borderTop: `1px solid ${brand.lineDark}`, pt: 3 }}>
                                <Mono color={brand.accent}>{pad(i + 1)}</Mono>
                                <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{block.title}</Typography>
                                <Typography sx={{ mt: 2, fontSize: '0.9375rem', lineHeight: 1.9, color: 'rgba(255, 255, 255, 0.72)', whiteSpace: 'pre-line' }}>{block.description}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ mt: { xs: 8, md: 10 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: { xs: 6, md: 8 } }}>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 3 }}>{t('home.model.judgmentsTitle')}</Typography>
                            <MarkerList items={list<string>('home.model.judgments')} tone="dark" />
                        </Box>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 700, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{t('home.model.structure.title')}</Typography>
                            {/* 開発体制の比較図 */}
                            <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>
                                {(['conventional', 'ours'] as const).map((key) => {
                                    const ours = key === 'ours';
                                    const roles = list<string>(`home.model.structure.${key}`);
                                    return (
                                        <Box key={key} sx={{ p: { xs: 2.5, md: 3 }, border: `1px solid ${ours ? brand.accent : brand.lineDark}`, backgroundColor: ours ? 'rgba(69, 182, 175, 0.08)' : 'transparent' }}>
                                            <Mono color={ours ? brand.accent : 'rgba(255, 255, 255, 0.55)'}>{t(`home.model.structure.${key}Label`)}</Mono>
                                            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                                {roles.map((role, i) => (
                                                    <React.Fragment key={role}>
                                                        {i > 0 && <Mono color="rgba(255, 255, 255, 0.4)">{ours ? '+' : '→'}</Mono>}
                                                        <Box sx={{ fontFamily: brand.fontMono, fontSize: '0.8125rem', px: 1.25, py: 0.5, border: `1px solid ${ours ? brand.accent : brand.lineDark}`, color: ours ? '#ffffff' : 'rgba(255, 255, 255, 0.72)' }}>
                                                            {role}
                                                        </Box>
                                                    </React.Fragment>
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Typography sx={{ mt: 2.5, fontSize: '0.875rem', lineHeight: 1.9, color: 'rgba(255, 255, 255, 0.72)' }}>{t('home.model.structure.caption')}</Typography>
                        </Box>
                    </Box>
                </Section>

                {/* 5. 短納期・適正価格を実現する仕組み */}
                <Section id="why" tone="alt">
                    <SectionHeading index="04" eyebrow={t('home.why.eyebrow')} title={t('home.why.title')} lead={t('home.why.lead')} />
                    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, borderTop: `1px solid ${brand.ink}` }}>
                        {list<Described>('home.why.reasons').map((reason, i) => (
                            <Box
                                component="li"
                                key={reason.title}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '40px 1fr', md: '80px 320px 1fr' },
                                    columnGap: 2,
                                    rowGap: 1,
                                    py: { xs: 3, md: 4 },
                                    borderBottom: `1px solid ${brand.line}`,
                                }}
                            >
                                <Mono color={brand.accentText} size="0.875rem">{pad(i + 1)}</Mono>
                                <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{reason.title}</Typography>
                                <Typography sx={{ gridColumn: { xs: '2', md: 'auto' }, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>
                                    {reason.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ mt: { xs: 6, md: 8 }, maxWidth: 760, pl: 2.5, borderLeft: `2px solid ${brand.accent}`, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 2, fontWeight: 500 }}>
                        {t('home.why.aiNote')}
                    </Typography>
                </Section>

                {/* 6. 料金 */}
                <Section id="pricing">
                    <SectionHeading index="05" eyebrow={t('home.pricing.eyebrow')} title={t('home.pricing.title')} lead={t('home.pricing.lead')} />

                    {/* 見積式 */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1, md: 2 } }}>
                        {list<string>('home.pricing.formula').map((term, i) => (
                            <React.Fragment key={term}>
                                {i > 0 && <Box sx={{ textAlign: 'center' }}><Mono size="1.25rem" color={brand.muted}>+</Mono></Box>}
                                <Box sx={{ flex: 1, border: `1px solid ${brand.ink}`, px: 3, py: 2.5, textAlign: 'center', fontWeight: 500 }}>{term}</Box>
                            </React.Fragment>
                        ))}
                    </Box>
                    <Typography sx={{ mt: 2, fontSize: '0.875rem', color: brand.inkSub }}>{t('home.pricing.formulaNote')}</Typography>

                    {/* 基本料金 */}
                    <Box sx={{ mt: { xs: 8, md: 10 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: { xs: 3, md: 6 }, borderTop: `1px solid ${brand.ink}`, pt: 4 }}>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700 }}>{t('home.pricing.base.label')}</Typography>
                            <Typography sx={{ mt: 1, fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 500, letterSpacing: '0.02em' }}>{t('home.pricing.base.price')}</Typography>
                            <Typography sx={{ mt: 2, fontSize: '0.875rem', lineHeight: 1.9, color: brand.inkSub }}>{t('home.pricing.base.description')}</Typography>
                        </Box>
                        <MarkerList items={list<string>('home.pricing.base.items')} columns={{ xs: 2, md: 3 }} />
                    </Box>

                    {/* 画面料金 */}
                    <Box sx={{ mt: { xs: 8, md: 10 }, borderTop: `1px solid ${brand.ink}`, pt: 4 }}>
                        <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700 }}>{t('home.pricing.screens.title')}</Typography>
                        <Typography sx={{ mt: 1, fontSize: '0.875rem', color: brand.inkSub }}>{t('home.pricing.screens.description')}</Typography>
                        <Box role="table" sx={{ mt: 3 }}>
                            <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '120px 200px 1fr', py: 1.5, borderBottom: `1px solid ${brand.line}` }}>
                                {list<string>('home.pricing.screens.headers').map((h) => (
                                    <Box role="columnheader" key={h}><Mono>{h}</Mono></Box>
                                ))}
                            </Box>
                            {list<ScreenRow>('home.pricing.screens.rows').map((row) => (
                                <Box
                                    role="row"
                                    key={row.size}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '64px 1fr', md: '120px 200px 1fr' },
                                        alignItems: 'baseline',
                                        rowGap: 0.5,
                                        py: 2.5,
                                        borderBottom: `1px solid ${brand.line}`,
                                    }}
                                >
                                    <Box role="cell"><Mono size="1rem" color={brand.ink}>{row.size}</Mono></Box>
                                    <Box role="cell" sx={{ fontSize: '1.125rem', fontWeight: 500 }}>{row.price}</Box>
                                    <Box role="cell" sx={{ gridColumn: { xs: '2', md: 'auto' }, fontSize: '0.875rem', lineHeight: 1.8, color: brand.inkSub }}>{row.example}</Box>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ mt: 4, ...cardSx, backgroundColor: brand.paperAlt, border: 'none' }}>
                            <Typography component="h4" sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>{t('home.pricing.screens.includesTitle')}</Typography>
                            <Typography sx={{ mt: 1, mb: 2.5, fontSize: '0.875rem', lineHeight: 1.8, color: brand.inkSub }}>{t('home.pricing.screens.includesLead')}</Typography>
                            <MarkerList items={list<string>('home.pricing.screens.includes')} columns={{ xs: 1, sm: 2, md: 3 }} />
                        </Box>
                    </Box>

                    {/* スマートフォンアプリ */}
                    <Box sx={{ mt: { xs: 8, md: 10 }, borderTop: `1px solid ${brand.ink}`, pt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: { xs: 3, md: 6 } }}>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700 }}>{t('home.pricing.mobile.title')}</Typography>
                            <Typography sx={{ mt: 2, fontSize: '0.875rem', lineHeight: 1.9, color: brand.inkSub }}>{t('home.pricing.mobile.description')}</Typography>
                        </Box>
                        <Box>
                            <Typography component="h4" sx={{ fontSize: '0.9375rem', fontWeight: 700, mb: 2.5 }}>{t('home.pricing.mobile.optionsTitle')}</Typography>
                            <MarkerList items={list<string>('home.pricing.mobile.options')} columns={{ xs: 1, sm: 2 }} variant="dash" />
                        </Box>
                    </Box>
                </Section>

                {/* 7. 料金シミュレーション例 */}
                <Section id="simulation" tone="alt">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: { xs: 0, md: 8 } }}>
                        <Box>
                            <SectionHeading index="06" eyebrow={t('home.simulation.eyebrow')} title={t('home.simulation.title')} lead={t('home.simulation.caption')} />
                        </Box>
                        <Box sx={cardSx}>
                            <Mono color={brand.accentText}>{t('home.simulation.subtitle')}</Mono>
                            <Box role="table" sx={{ mt: 2 }}>
                                <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', py: 1.5, borderBottom: `1px solid ${brand.line}` }}>
                                    {list<string>('home.simulation.headers').map((h, i) => (
                                        <Box role="columnheader" key={h} sx={{ textAlign: i === 0 ? 'left' : 'right' }}><Mono>{h}</Mono></Box>
                                    ))}
                                </Box>
                                {list<SimulationRow>('home.simulation.rows').map((row) => (
                                    <Box role="row" key={row.item} sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', py: 1.75, borderBottom: `1px solid ${brand.line}`, fontSize: '0.9375rem' }}>
                                        <Box role="cell">{row.item}</Box>
                                        <Box role="cell" sx={{ textAlign: 'right', color: brand.inkSub }}>{row.quantity}</Box>
                                        <Box role="cell" sx={{ textAlign: 'right' }}>{row.amount}</Box>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>{t('home.simulation.totalLabel')}</Typography>
                                <Typography sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 500, letterSpacing: '0.02em' }}>{t('home.simulation.total')}</Typography>
                            </Box>
                            <Typography sx={{ mt: 2, fontSize: '0.75rem', lineHeight: 1.8, color: brand.muted }}>{t('home.simulation.note')}</Typography>
                        </Box>
                    </Box>
                </Section>

                {/* 8. 開発実績 */}
                <Section id="works" tone="dark">
                    <SectionHeading index="07" eyebrow={t('home.works.eyebrow')} title={t('home.works.title')} lead={t('home.works.lead')} tone="dark" />

                    {/* 期間・規模・対応領域のまとめ */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, borderTop: `1px solid ${brand.lineDark}`, borderBottom: `1px solid ${brand.lineDark}` }}>
                        {list<Stat>('home.works.summary').map((stat, i) => (
                            <Box key={stat.label} sx={{ py: 3, px: { xs: 0, sm: 3 }, borderLeft: { sm: i > 0 ? `1px solid ${brand.lineDark}` : 'none' }, borderTop: { xs: i > 0 ? `1px solid ${brand.lineDark}` : 'none', sm: 'none' } }}>
                                <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.875rem' }, fontWeight: 500, letterSpacing: '0.02em' }}>{stat.value}</Typography>
                                <Typography sx={{ mt: 0.5, fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>{stat.label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* 案件一覧 */}
                    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: { xs: 6, md: 8 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {list<WorkItem>('home.works.items').map((work, i) => (
                            <Box
                                component="li"
                                key={work.name}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '5fr 3fr 6fr' },
                                    gap: { xs: 3, md: 5 },
                                    p: { xs: 3, md: 4 },
                                    border: `1px solid ${brand.lineDark}`,
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                }}
                            >
                                <Box>
                                    <Mono color={brand.accent}>{`${pad(i + 1)} / ${work.type}`}</Mono>
                                    <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.5 }}>{work.name}</Typography>
                                    <Typography sx={{ mt: 1.5, fontSize: '0.875rem', lineHeight: 1.9, color: 'rgba(255, 255, 255, 0.72)' }}>{work.description}</Typography>
                                </Box>
                                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start', gap: 2, pt: { xs: 2.5, md: 0 }, borderTop: { xs: `1px solid ${brand.lineDark}`, md: 'none' } }}>
                                    {([['periodLabel', work.period], ['budgetLabel', work.budget]] as const).map(([labelKey, value]) => (
                                        <Box key={labelKey}>
                                            <Box component="dt" sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>{t(`home.works.${labelKey}`)}</Box>
                                            <Box component="dd" sx={{ m: 0, mt: 0.5, fontSize: { xs: '1.375rem', md: '1.5rem' }, fontWeight: 500, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{value}</Box>
                                        </Box>
                                    ))}
                                </Box>
                                <Box component="ul" aria-label="Technology" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 1 }}>
                                    {work.tech.map((item) => (
                                        <Box component="li" key={item} sx={{ fontFamily: brand.fontMono, fontSize: '0.8125rem', px: 1.25, py: 0.5, border: `1px solid ${brand.lineDark}`, color: 'rgba(255, 255, 255, 0.86)' }}>
                                            {item}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ mt: 3, fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>{t('home.works.note')}</Typography>
                </Section>

                {/* 9. 開発範囲 */}
                <Section id="scope">
                    <SectionHeading index="08" eyebrow={t('home.scope.eyebrow')} title={t('home.scope.title')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 }}>
                        <Box sx={{ ...cardSx, borderColor: brand.ink }}>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 3 }}>{t('home.scope.included.title')}</Typography>
                            <MarkerList items={list<string>('home.scope.included.items')} />
                        </Box>
                        <Box sx={cardSx}>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 3 }}>{t('home.scope.excluded.title')}</Typography>
                            <MarkerList items={list<string>('home.scope.excluded.items')} columns={{ xs: 1, sm: 2 }} variant="dash" />
                        </Box>
                    </Box>
                    <Typography sx={{ mt: 3, fontSize: '0.8125rem', lineHeight: 1.8, color: brand.muted }}>{t('home.scope.note')}</Typography>
                </Section>

                {/* 10. 技術スタック */}
                <Section id="tech" tone="alt">
                    <SectionHeading index="09" eyebrow={t('home.tech.eyebrow')} title={t('home.tech.title')} lead={t('home.tech.lead')} />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                            borderTop: `1px solid ${brand.line}`,
                            borderLeft: `1px solid ${brand.line}`,
                            backgroundColor: brand.paper,
                        }}
                    >
                        {list<TechGroup>('home.tech.groups').map((group) => (
                            <Box key={group.label} sx={{ p: 3, borderRight: `1px solid ${brand.line}`, borderBottom: `1px solid ${brand.line}` }}>
                                <Mono>{group.label}</Mono>
                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {group.items.map((item) => (
                                        <Box key={item} sx={{ fontFamily: brand.fontMono, fontSize: '0.875rem', px: 1.25, py: 0.5, border: `1px solid ${brand.line}`, backgroundColor: brand.paperAlt }}>
                                            {item}
                                        </Box>
                                    ))}
                                </Box>
                                {group.note && <Typography sx={{ mt: 1.5, fontSize: '0.75rem', color: brand.muted }}>{group.note}</Typography>}
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ mt: 2, fontSize: '0.8125rem', lineHeight: 1.8, color: brand.muted }}>{t('home.tech.mobileNote')}</Typography>

                    <Box sx={{ mt: { xs: 6, md: 8 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, md: 6 }, borderTop: `1px solid ${brand.ink}`, pt: 4 }}>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{t('home.tech.docs.title')}</Typography>
                            <Typography sx={{ mt: 2, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>{t('home.tech.docs.description')}</Typography>
                        </Box>
                        <MarkerList items={list<string>('home.tech.docs.benefits')} />
                    </Box>
                </Section>

                {/* 11. 品質・テスト方針 */}
                <Section id="quality">
                    <SectionHeading index="10" eyebrow={t('home.quality.eyebrow')} title={t('home.quality.title')} lead={t('home.quality.lead')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                        {list<Described>('home.quality.items').map((item, i) => (
                            <Box key={item.title} sx={cardSx}>
                                <Mono color={brand.accentText}>{pad(i + 1)}</Mono>
                                <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.0625rem', fontWeight: 700 }}>{item.title}</Typography>
                                <Typography sx={{ mt: 1.5, fontSize: '0.875rem', lineHeight: 1.9, color: brand.inkSub }}>{item.description}</Typography>
                            </Box>
                        ))}
                        <Box sx={{ ...cardSx, backgroundColor: brand.paperAlt, border: 'none' }}>
                            <Mono color={brand.accentText}>QA</Mono>
                            <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.0625rem', fontWeight: 700 }}>{t('home.quality.qa.title')}</Typography>
                            <Typography sx={{ mt: 1.5, fontSize: '0.875rem', lineHeight: 1.9, color: brand.inkSub }}>{t('home.quality.qa.description')}</Typography>
                        </Box>
                    </Box>
                </Section>

                {/* 12. 開発フロー */}
                <Section id="flow" tone="alt">
                    <SectionHeading index="11" eyebrow={t('home.flow.eyebrow')} title={t('home.flow.title')} lead={t('home.flow.lead')} />
                    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, columnGap: 3, rowGap: { xs: 0, sm: 4 } }}>
                        {list<string>('home.flow.steps').map((step, i) => (
                            <Box
                                component="li"
                                key={step}
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'row', sm: 'column' },
                                    alignItems: { xs: 'baseline', sm: 'flex-start' },
                                    gap: { xs: 2, sm: 1.5 },
                                    borderTop: `1px solid ${brand.ink}`,
                                    pt: 2,
                                    pb: { xs: 2.5, sm: 0 },
                                }}
                            >
                                <Mono color={brand.accentText} size="0.875rem">{pad(i + 1)}</Mono>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                    <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 500 }}>{step}</Typography>
                                    {i >= 1 && i <= 3 && (
                                        <Box component="span" sx={{ fontSize: '0.6875rem', px: 1, py: 0.25, border: `1px solid ${brand.accentText}`, color: brand.accentText }}>
                                            {t('home.flow.free')}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Section>

                {/* 13. 保守・追加開発 */}
                <Section id="maintenance">
                    <SectionHeading index="12" eyebrow={t('home.maintenance.eyebrow')} title={t('home.maintenance.title')} lead={t('home.maintenance.lead')} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: { xs: 3, md: 6 }, borderTop: `1px solid ${brand.ink}`, pt: 4 }}>
                        <Box>
                            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700 }}>{t('home.maintenance.priceLabel')}</Typography>
                            <Typography sx={{ mt: 1, fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 500, letterSpacing: '0.02em' }}>{t('home.maintenance.price')}</Typography>
                        </Box>
                        <Box>
                            <MarkerList items={list<string>('home.maintenance.items')} columns={{ xs: 1, sm: 2 }} />
                            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {list<string>('home.maintenance.notes').map((note) => (
                                    <Typography key={note} sx={{ fontSize: '0.8125rem', lineHeight: 1.8, color: brand.muted }}>※ {note}</Typography>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Section>

                {/* 14. 年間受注枠について */}
                <Section id="capacity" tone="dark" grid>
                    <Eyebrow index="13" label={t('home.capacity.eyebrow')} tone="dark" />
                    <Typography component="h2" sx={{ mt: 2.5, fontSize: { xs: '1.5rem', md: '2.75rem' }, fontWeight: 700, lineHeight: 1.45, letterSpacing: '0.03em', whiteSpace: 'pre-line' }}>
                        {t('home.capacity.title')}
                    </Typography>
                    <Lead tone="dark">{t('home.capacity.description')}</Lead>
                    <Typography sx={{ mt: 2, fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>{t('home.capacity.note')}</Typography>
                </Section>

                {/* 15. FAQ */}
                <Section id="faq">
                    <SectionHeading index="14" eyebrow={t('home.faq.eyebrow')} title={t('home.faq.title')} />
                    <Box sx={{ borderTop: `1px solid ${brand.ink}` }}>
                        {list<FaqItem>('home.faq.items').map((item, i) => (
                            <Accordion
                                key={item.question}
                                disableGutters
                                elevation={0}
                                square
                                sx={{ borderBottom: `1px solid ${brand.line}`, backgroundColor: 'transparent', '&::before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1, '& .MuiAccordionSummary-content': { gap: 2, alignItems: 'baseline' } }}>
                                    <Mono color={brand.accentText}>Q{pad(i + 1)}</Mono>
                                    <Typography component="h3" sx={{ fontSize: { xs: '0.9375rem', md: '1.0625rem' }, fontWeight: 500, lineHeight: 1.7 }}>{item.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 0, pt: 0, pb: 3, pl: { xs: 0, md: 6 } }}>
                                    <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>{item.answer}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Section>

                {/* 16. Contact CTA */}
                <Section id="contact" tone="dark" grid>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, alignItems: 'end', gap: { xs: 5, md: 8 } }}>
                        <Box>
                            <Eyebrow label={t('home.cta.eyebrow')} tone="dark" />
                            <Typography component="h2" sx={{ mt: 2.5, fontSize: { xs: '1.5rem', md: '2.5rem' }, fontWeight: 700, lineHeight: 1.5, letterSpacing: '0.03em', whiteSpace: 'pre-line' }}>
                                {t('home.cta.title')}
                            </Typography>
                            <Lead tone="dark">{t('home.cta.description')}</Lead>
                        </Box>
                        <Box>
                            <CtaButton href="/contact" tone="dark">{t('home.cta.button')}</CtaButton>
                        </Box>
                    </Box>
                </Section>
            </Box>

            <Footer />
        </Box>
    );
};

export default HomePage;
